import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header/Header';
import SEO from '../components/SEO/SEO';
import RsiHeatmapGrid from '../components/RsiHeatmap/RsiHeatmapGrid';
import './RsiHeatmapPage.css';

function RsiHeatmapPage() {
  const { t } = useTranslation();
  const [expandedFaq, setExpandedFaq] = useState(null);

  const faqData = [
    {
      question: t('rsi.faq.q1', 'What is RSI (Relative Strength Index)?'),
      answer: t('rsi.faq.a1', 'RSI is a momentum oscillator that measures the speed and magnitude of price changes. It ranges from 0 to 100, with values above 70 indicating overbought conditions and values below 30 indicating oversold conditions.')
    },
    {
      question: t('rsi.faq.q2', 'How to read the RSI Heatmap?'),
      answer: t('rsi.faq.a2', 'The heatmap displays RSI values using colors: Red indicates overbought conditions (RSI > 70), green indicates oversold conditions (RSI < 30), and neutral colors show balanced market conditions. Click on any coin to see detailed RSI data.')
    },
    {
      question: t('rsi.faq.q3', 'What timeframe does the RSI use?'),
      answer: t('rsi.faq.a3', 'Our RSI heatmap uses the standard 14-period RSI calculation on the 4-hour timeframe, which is optimal for identifying medium-term trading opportunities in cryptocurrency markets.')
    },
    {
      question: t('rsi.faq.q4', 'Is RSI reliable for crypto trading?'),
      answer: t('rsi.faq.a4', 'RSI is a useful tool but should not be used in isolation. Cryptocurrency markets are highly volatile, so combining RSI with other indicators like volume, moving averages, and market sentiment provides better trading signals.')
    }
  ];

  // Add FAQ Schema to page
  useEffect(() => {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqData.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };

    // Create or update FAQ schema script
    let faqScript = document.querySelector('script[data-schema="faq"]');
    if (!faqScript) {
      faqScript = document.createElement('script');
      faqScript.setAttribute('type', 'application/ld+json');
      faqScript.setAttribute('data-schema', 'faq');
      document.head.appendChild(faqScript);
    }
    faqScript.textContent = JSON.stringify(faqSchema);

    // Cleanup on unmount
    return () => {
      const script = document.querySelector('script[data-schema="faq"]');
      if (script) script.remove();
    };
  }, [faqData]);

  return (
    <div className="rsi-heatmap-page">
      <SEO page="rsi" />
      <Header />

      <div className="rsi-page-content">
        <h1 className="rsi-title">{t('rsi.pageTitle', 'Crypto RSI Heatmap')}</h1>
        <p className="rsi-subtitle">{t('rsi.pageSubtitle', 'Real-time RSI values for top 100 cryptocurrencies')}</p>

        <RsiHeatmapGrid />

        {/* SEO Content Section */}
        <section className="rsi-seo-section">
          <div className="rsi-info-grid">
            <article className="rsi-info-card">
              <h2>{t('rsi.whatIsRsi', 'What is RSI?')}</h2>
              <p>
                {t('rsi.whatIsRsiText', 'The Relative Strength Index (RSI) is a momentum indicator developed by J. Welles Wilder in 1978. It measures the speed and magnitude of recent price changes to evaluate overbought or oversold conditions. RSI oscillates between 0 and 100, providing traders with actionable signals for potential market reversals.')}
              </p>
            </article>

            <article className="rsi-info-card">
              <h2>{t('rsi.howToUse', 'How to Use RSI Heatmap')}</h2>
              <p>
                {t('rsi.howToUseText', 'Our crypto RSI heatmap visualizes RSI values for 100+ cryptocurrencies in real-time. Look for oversold conditions (RSI < 30) for potential buying opportunities, and overbought conditions (RSI > 70) for potential selling signals. The color-coded display makes it easy to spot opportunities at a glance.')}
              </p>
            </article>

            <article className="rsi-info-card">
              <h2>{t('rsi.rsiLevels', 'RSI Signal Levels')}</h2>
              <ul className="rsi-levels-list">
                <li><span className="rsi-extreme-oversold">RSI &lt; 20</span> — {t('rsi.extremeOversoldDesc', 'Extreme oversold - Strong buy signal')}</li>
                <li><span className="rsi-oversold">RSI 20-30</span> — {t('rsi.oversoldDesc', 'Oversold - Potential reversal zone')}</li>
                <li><span className="rsi-neutral">RSI 30-70</span> — {t('rsi.neutralDesc', 'Neutral - No clear signal')}</li>
                <li><span className="rsi-overbought">RSI 70-80</span> — {t('rsi.overboughtDesc', 'Overbought - Potential reversal zone')}</li>
                <li><span className="rsi-extreme-overbought">RSI &gt; 80</span> — {t('rsi.extremeOverboughtDesc', 'Extreme overbought - Strong sell signal')}</li>
              </ul>
            </article>
          </div>

          {/* FAQ Section */}
          <div className="rsi-faq-section">
            <h2>{t('rsi.faqTitle', 'Frequently Asked Questions')}</h2>
            <div className="rsi-faq-list">
              {faqData.map((faq, index) => (
                <div
                  key={index}
                  className={`rsi-faq-item ${expandedFaq === index ? 'expanded' : ''}`}
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                >
                  <div className="rsi-faq-question">
                    <h3>{faq.question}</h3>
                    <span className="rsi-faq-toggle">{expandedFaq === index ? '−' : '+'}</span>
                  </div>
                  {expandedFaq === index && (
                    <div className="rsi-faq-answer">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default RsiHeatmapPage;
